import type { Home, HomeDto } from '@lmc/ts-common'
import {
  DownloadFile,
  EmptyState,
  FilterContainer,
  GroupedResidentsMultiSelect,
  NoDataIcon,
  PageHeader,
  RangePicker
} from '@lmc/ui-kit'
import type { TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { canManagePayAndInvoicing } from 'common/permissions'
import { getNominative } from 'common/utils'
import { Spinner } from 'components/generic'
import dayjs, { type Dayjs } from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import { useGetInvoicingSettings } from 'screens/Rostering/hooks/useInvoicingRates'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import {
  getReconciliationRangeDisabledDate,
  isValidReconciliationDateRange,
  RECONCILIATION_MAX_PAST_MONTHS
} from 'screens/Rostering/Reconciliation/reconciliationUtils'
import { useChargeReport } from '../hooks/useChargeReport'
import { PayReportCsvButton } from '../PayReport/PayReportBreakdownFooter'
import {
  payReportTableComponents,
  StyledPayReportTable
} from '../PayReport/PayReportTableComponents'
import {
  ContentContainer,
  Filters,
  LeftContainer,
  LoaderWrap,
  MainContainer,
  RightAlign,
  StyledMultiSelect,
  TableScroll
} from '../PayReport/payReportPageStyles'
import type {
  ChargeReportRow,
  ChargeReportSortField,
  ChargeReportToReconciliationNavigationState
} from '../types/chargeReport'
import { ChargeReportBreakdownDrawer } from './ChargeReportBreakdownDrawer'
import { getChargeReportColumns } from './chargeReportColumns'
import { DownloadChargeReportModal } from './DownloadChargeReportModal'

const PAGE_SIZE = 10

const ChargeReport = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const sendEvent = useMixpanelEvent()

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs().subtract(1, 'day')
  ])
  const [serviceUserIds, setServiceUserIds] = useState<string[]>([])
  const [fundingTypeIds, setFundingTypeIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [sortField, setSortField] =
    useState<ChargeReportSortField>('serviceUser')
  const [sortDir, setSortDir] = useState<1 | -1>(1)
  const [breakdownRow, setBreakdownRow] = useState<ChargeReportRow | null>(null)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const serviceUserLabel = getNominative(preferredPeopleNominative, 'Resident')

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const { residents } = useResidentsList({})
  const fundingTypeOptions =
    home.rotaSettings?.fundingTypes
      ?.filter((fundingType) => fundingType.id && !fundingType.isDeleted)
      .map((fundingType) => ({
        label: fundingType.name,
        value: fundingType.id as string
      })) ?? []

  const userRole = useSelector(
    (state: { global: { user: { role: string } } }) => state.global.user?.role
  )

  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: ['invoicing_reconciliation_report']
  })
  // Charge rates are enabled per-home via the invoicing settings toggle, so
  // require it too — matching the Charge Rates reconciliation tab's gate.
  const { data: invoicingSettings, isLoading: isInvoicingLoading } =
    useGetInvoicingSettings()
  // Charge Report is available to admins and management, matching the
  // reconciliation screen's gate.
  const chargeReportEnabled =
    flags?.invoicing_reconciliation_report?.enabled === true &&
    canManagePayAndInvoicing(userRole) &&
    (invoicingSettings?.enabled ?? false)

  // Fire the "report opened" event once, when the report is actually viewable.
  const hasTrackedOpen = useRef(false)
  useEffect(() => {
    if (chargeReportEnabled && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true
      sendEvent(MixpanelEvents.CHARGE_REPORT_OPENED, {})
    }
  }, [chargeReportEnabled, sendEvent])

  const rangeValid = useMemo(
    () => isValidReconciliationDateRange(dateRange[0], dateRange[1]),
    [dateRange]
  )

  const navigateToReconciliation = useCallback(
    (row: ChargeReportRow) => {
      if (!isValidReconciliationDateRange(dateRange[0], dateRange[1])) {
        return
      }
      navigate('/rota/reconciliation', {
        state: {
          fromChargeReport: true,
          startTime: dateRange[0].startOf('day').toISOString(),
          endTime: dateRange[1].endOf('day').toISOString(),
          serviceUserIds: [row.serviceUser.id],
          ...(row.fundingType ? { fundingTypeIds: [row.fundingType.id] } : {}),
          status: 'needs_attention'
        } satisfies ChargeReportToReconciliationNavigationState
      })
    },
    [dateRange, navigate]
  )

  const apiFilters = useMemo(() => {
    if (!rangeValid) {
      return null
    }
    return {
      startTime: dateRange[0].startOf('day').toISOString(),
      endTime: dateRange[1].endOf('day').toISOString(),
      ...(serviceUserIds.length ? { serviceUserIds } : {}),
      ...(fundingTypeIds.length ? { fundingTypeIds } : {}),
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortDir
    }
  }, [
    rangeValid,
    dateRange,
    serviceUserIds,
    fundingTypeIds,
    page,
    sortField,
    sortDir
  ])

  const queryEnabled = Boolean(chargeReportEnabled && apiFilters !== null)
  const { data, isLoading, isFetching } = useChargeReport(
    apiFilters,
    queryEnabled
  )
  const rows = data?.data ?? []
  const total = data?.pagination?.totalItems ?? 0

  const columns = useMemo(
    () =>
      getChargeReportColumns({
        t,
        nominative: serviceUserLabel,
        sortField,
        sortDir,
        onNeedsAttentionClick: navigateToReconciliation
      }),
    [t, serviceUserLabel, sortField, sortDir, navigateToReconciliation]
  )

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      return
    }
    if (!isValidReconciliationDateRange(dates[0], dates[1])) {
      return
    }
    setDateRange([dates[0], dates[1]])
    setPage(1)
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<ChargeReportRow> | SorterResult<ChargeReportRow>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => {
    if (extra.action === 'paginate') {
      if (pagination.current != null) {
        setPage(pagination.current)
      }
    } else {
      setPage(1)
    }
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.columnKey && s.order) {
      setSortField(String(s.columnKey) as ChargeReportSortField)
      setSortDir(s.order === 'ascend' ? 1 : -1)
    }
  }

  if (isFlagsLoading || isInvoicingLoading) {
    return (
      <LoaderWrap>
        <Spinner />
      </LoaderWrap>
    )
  }

  if (!chargeReportEnabled) {
    return <Navigate to="/reports/rostering/rota-overview" replace />
  }

  return (
    <MainContainer>
      <PageHeader title={t('CHARGE_REPORT_TITLE')} />
      <FilterContainer title={t('FILTERS')}>
        <Filters>
          <LeftContainer>
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              disabledDate={getReconciliationRangeDisabledDate}
              allowClear={false}
              format="DD-MM-YYYY"
              minDate={dayjs().subtract(
                RECONCILIATION_MAX_PAST_MONTHS,
                'month'
              )}
              maxDate={dayjs()}
            />
            <GroupedResidentsMultiSelect
              data-testid="charge-report-service-users-select"
              residents={residents}
              title={serviceUserLabel}
              values={serviceUserIds}
              handleInputChange={(selected) => {
                setServiceUserIds(selected as string[])
                setPage(1)
              }}
            />
            <StyledMultiSelect
              data-testid="charge-report-funding-types-select"
              title={t('CHARGE_REPORT_COL_FUNDING_TYPE')}
              options={fundingTypeOptions}
              values={fundingTypeIds}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selected) => {
                setFundingTypeIds(selected as string[])
                setPage(1)
              }}
            />
          </LeftContainer>
          <RightAlign>
            <PayReportCsvButton
              icon={<DownloadFile />}
              onClick={() => setDownloadModalOpen(true)}
            >
              {t('PAY_REPORT_DOWNLOAD_CSV')}
            </PayReportCsvButton>
          </RightAlign>
        </Filters>
      </FilterContainer>

      <ContentContainer>
        {!rangeValid ? (
          <EmptyState
            title={t('PAY_REPORT_INVALID_RANGE')}
            icon={<NoDataIcon />}
          />
        ) : (isLoading || isFetching) && !rows.length ? (
          <LoaderWrap>
            <Spinner />
          </LoaderWrap>
        ) : !rows.length && !isFetching ? (
          <EmptyState title={t('NO_DATA_TO_SHOW')} icon={<NoDataIcon />} />
        ) : (
          <TableScroll>
            <StyledPayReportTable
              components={payReportTableComponents}
              columns={columns}
              dataSource={rows}
              rowKey="id"
              loading={isFetching}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                position: ['bottomCenter']
              }}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              size="small"
              showSorterTooltip={false}
              sortDirections={['ascend', 'descend']}
              rowClassName={(_, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              onRow={(record) => ({
                'data-testid': 'charge-report-row',
                onClick: () => setBreakdownRow(record),
                style: { cursor: 'pointer' }
              })}
            />
          </TableScroll>
        )}
      </ContentContainer>

      <ChargeReportBreakdownDrawer
        key={
          breakdownRow
            ? `${breakdownRow.id}-${dateRange[0].valueOf()}-${dateRange[1].valueOf()}`
            : 'charge-report-breakdown-closed'
        }
        open={breakdownRow !== null}
        onClose={() => setBreakdownRow(null)}
        dateRange={dateRange}
        row={breakdownRow}
      />
      <DownloadChargeReportModal
        isVisible={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        initialDateRange={dateRange}
        initialServiceUserIds={serviceUserIds}
        initialFundingTypeIds={fundingTypeIds}
        serviceUserLabel={serviceUserLabel}
      />
    </MainContainer>
  )
}

export default ChargeReport
